import { Instagram, Linkedin, Youtube, Facebook, MessageCircle, ArrowUpRight } from "lucide-react";
import { useSite } from "@/lib/SiteContext";
import { useT } from "@/lib/i18n";
import { buildWhatsAppUrl, SITE } from "@/lib/constants";
import { trackWaClick, track } from "@/lib/track";
import Wordmark from "./Wordmark";

export const Footer = () => {
  const { locale } = useSite();
  const { t } = useT(locale);
  const year = new Date().getFullYear();

  const navLinks = [
    { href: "#lineas", label: t("nav.lineas") },
    { href: "#marketplace", label: t("nav.marketplace") },
    { href: "#diferencial", label: t("nav.diferencial") },
    { href: "#cobertura", label: t("nav.cobertura") },
    { href: "#proceso", label: t("nav.proceso") },
  ];

  return (
    <footer
      className="relative pt-0 pb-10"
      style={{ backgroundColor: "var(--ix-night)", color: "var(--ix-cream)" }}
      data-testid="site-footer"
    >
      {/* Top gradient marquee */}
      <div className="border-y border-[var(--ix-cream)]/10 py-4 overflow-hidden">
        <div className="ix-marquee ix-marquee-fast">
          {[...Array(2)].map((_, k) => (
            <div key={k} className="flex items-center gap-10 px-6 ix-display text-[1.6rem] md:text-[2rem]">
              <span className="ix-grad-text">Diseñamos tu próxima experiencia</span>
              <span className="text-[var(--ix-cream)]/30">◆</span>
              <span className="text-[var(--ix-cream)]">Hablar con el Lab</span>
              <span className="text-[var(--ix-cream)]/30">◆</span>
              <span className="ix-grad-text">Diseñamos tu próxima experiencia</span>
              <span className="text-[var(--ix-cream)]/30">◆</span>
              <span className="text-[var(--ix-cream)]">Hablar con el Lab</span>
              <span className="text-[var(--ix-cream)]/30">◆</span>
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-[1480px] mx-auto px-5 md:px-10 lg:px-14 pt-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <Wordmark tone="light" />
            <p className="mt-6 text-[0.95rem] text-[var(--ix-cream)]/65 max-w-sm leading-relaxed">
              {t("footer.tagline")}
            </p>
            <a
              href={buildWhatsAppUrl()}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackWaClick("footer")}
              className="ix-btn ix-btn-grad mt-7"
              data-testid="footer-whatsapp"
            >
              <MessageCircle size={15} strokeWidth={1.8} />
              {SITE.whatsappDisplay}
              <ArrowUpRight size={13} strokeWidth={1.8} />
            </a>
            <a
              href={`mailto:${SITE.email}`}
              onClick={() => track("email_click", { source: "footer", email: SITE.email })}
              className="mt-4 inline-flex items-center gap-2 ix-mono text-[0.78rem] tracking-[0.06em] text-[var(--ix-cream)]/70 hover:text-[var(--ix-cream)] transition-colors"
              data-testid="footer-email"
            >
              <span className="ix-grad-text font-semibold">▸</span>
              {SITE.email}
            </a>
          </div>

          <div className="md:col-span-3">
            <div className="ix-mono text-[0.66rem] uppercase tracking-[0.22em] text-[var(--ix-cream)]/55 mb-4">
              ▸ {t("footer.nav_title")}
            </div>
            <ul className="space-y-2">
              {navLinks.map((l) => (
                <li key={l.href}>
                  <a
                    href={l.href}
                    className="ix-link text-[0.94rem] text-[var(--ix-cream)]/85"
                    data-testid={`footer-nav-${l.href.replace("#", "")}`}
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="ix-mono text-[0.66rem] uppercase tracking-[0.22em] text-[var(--ix-cream)]/55 mb-4">
              ▸ {t("footer.legal_title")}
            </div>
            <ul className="space-y-2 text-[0.9rem] text-[var(--ix-cream)]/85">
              <li className="ix-mono text-[0.78rem]">{t("footer.rnt")}</li>
              <li><a href="#" className="ix-link">{t("footer.privacy")}</a></li>
              <li><a href="#" className="ix-link">{t("footer.terms")}</a></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="ix-mono text-[0.66rem] uppercase tracking-[0.22em] text-[var(--ix-cream)]/55 mb-4">
              ▸ {t("footer.social")}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <a href="https://www.instagram.com/incubatour/?hl=es" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="p-2 border border-[var(--ix-cream)]/15 hover:border-[var(--ix-cream)]/60 transition-colors" data-testid="social-instagram">
                <Instagram size={15} strokeWidth={1.5} />
              </a>
              <a href="https://www.linkedin.com/company/incubatour/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="p-2 border border-[var(--ix-cream)]/15 hover:border-[var(--ix-cream)]/60 transition-colors" data-testid="social-linkedin">
                <Linkedin size={15} strokeWidth={1.5} />
              </a>
              <a href="https://www.facebook.com/incubatour/?locale=es_LA" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="p-2 border border-[var(--ix-cream)]/15 hover:border-[var(--ix-cream)]/60 transition-colors" data-testid="social-facebook">
                <Facebook size={15} strokeWidth={1.5} />
              </a>
              <a href="https://www.youtube.com/@incubatour5012" target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="p-2 border border-[var(--ix-cream)]/15 hover:border-[var(--ix-cream)]/60 transition-colors" data-testid="social-youtube">
                <Youtube size={15} strokeWidth={1.5} />
              </a>
              <a href="https://www.tiktok.com/@incubatour" target="_blank" rel="noopener noreferrer" aria-label="TikTok" className="p-2 border border-[var(--ix-cream)]/15 hover:border-[var(--ix-cream)]/60 transition-colors text-[var(--ix-cream)]/85 ix-mono text-[0.7rem] tracking-[0.05em]" data-testid="social-tiktok" style={{ minWidth: 31, height: 31, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                TT
              </a>
              <a href="https://open.spotify.com/show/6mLqBjQl7AdCqR1Th9OJrm" target="_blank" rel="noopener noreferrer" aria-label="Spotify" className="p-2 border border-[var(--ix-cream)]/15 hover:border-[var(--ix-cream)]/60 transition-colors text-[var(--ix-cream)]/85 ix-mono text-[0.7rem] tracking-[0.05em]" data-testid="social-spotify" style={{ minWidth: 31, height: 31, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                SP
              </a>
            </div>
            <div className="mt-5 ix-mono text-[0.66rem] uppercase tracking-[0.18em] text-[var(--ix-cream)]/45">
              @incubatour
            </div>
          </div>
        </div>

        <div className="ix-rule-dark mt-16 h-px" />
        <div className="mt-6 flex flex-col md:flex-row gap-4 md:gap-0 md:items-center md:justify-between text-[0.78rem] text-[var(--ix-cream)]/55">
          <div className="ix-mono">
            {t("footer.copyright").replace("{year}", String(year))}
          </div>
          <div className="flex items-center gap-3">
            <span
              aria-hidden
              style={{
                width: 14, height: 18,
                background: "linear-gradient(135deg, #FF1F8F, #FCB424, #8AE52E)",
                clipPath: "polygon(22% 0%, 100% 0%, 78% 100%, 0% 100%)",
              }}
            />
            <span className="ix-mono uppercase tracking-[0.18em]">RNT 278668 · Colombia</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
